<template>
  <div class="row f-row h-100">
    <div
      class="col col-12 text-center progress-div"
      :style="'top: ' + progressPosition + '%!important;'"
      v-if="showLoading"
    >
      <QSLoading
        :isLoading="showLoadingSpot"
        :size="40"
        :colorTheme="colorTheme"
      />
    </div>
    <div
      class="col col-12 text-center"
      v-else-if="histData.length === 0 && fwdData.length === 0"
      style="position: absolute; left: 0; top: 27.5%; z-index: 10000000;"
    >
      No Data Available
    </div>
    <div class="col col-5" style="height:100%; width:100%">
      <div class="h-100 d-flex flex-column" :id="div1Name" style="width: 100%;">
        <svg :id="svg1Name"></svg>
      </div>
    </div>
    <div class="col col-7" style="height:100%; width:100%">
      <div class="h-100 d-flex flex-column" :id="div2Name" style="width: 100%;">
        <svg :id="svg2Name"></svg>
      </div>
    </div>
  </div>
</template>

<script>
import {
  colorExpo,
  currencyFormat,
  dateFormat,
  grayLightBackground,
} from "@/_core/_helpers/_parametric_tools";
import { D3Chart } from "@/_core/charts/D3Chart";
import { D3HoverMktHist } from "@/_core/charts/D3ChartMarketshover";
import { format } from "d3";
import QSLoading from "@/components/QSLoading.vue";

export default {
  name: "CPOPastAndFwdsChart",
  components: {
    QSLoading,
  },
  props: {
    allData: { required: true, default: () => [] },
    colorTheme: { required: true, type: Boolean, default: true },
    div1Name: { required: true },
    div2Name: { required: true },
    fwdColor: { required: true },
    fwdData: { required: true, default: () => [] },
    histData: { required: true, default: () => [] },
    hoverName: { required: true },
    interval: { required: true },
    isCandles: { required: true, default: () => [] },
    isFEP: { required: false, default: false },
    monthData: { required: true, default: () => [] },
    progressPosition: { required: true },
    renderChartsSignal: { required: true, default: 0 },
    showLoading: { required: true, type: Boolean, default: false },
    svg1Name: { required: true },
    svg2Name: { required: true },
    // ============ Data Classes ===============
    processData: { required: true },
  },
  data() {
    return {
      D3ChartFwd: null,
      D3ChartHist: null,
      D3HoverMktHist: null,
      D3HoverMktHistb: null,
      minMaxYAxis: [-Infinity, +Infinity],
      monthDate: "%b%y",
      svgNodeFwd: null,
      svgNodeHist: null,
    };
  },
  created() {
    this.D3HoverMktHist = new D3HoverMktHist();
    this.D3HoverMktHistb = new D3HoverMktHist();
    this.D3ChartHist = new D3Chart(this.div1Name, this.svg1Name);
    this.D3ChartFwd = new D3Chart(this.div2Name, this.svg2Name);
  },
  mounted() {
    let _this = this;
    window.addEventListener("resize", function() {
      _this.callWidth();
    });
    this.callWidth();
  },
  watch: {
    colorTheme() {
      this.callWidth();
    },
    showLoading(val) {
      if (val === false) {
        this.renderCharts();
      }
    },
    renderChartsSignal() {
      this.callWidth();
    },
  },
  methods: {
    callWidth() {
      if (this.histData.length > 0 && this.fwdData.length > 0) {
        this.renderCharts();
      }
    },
    renderCharts() {
      // === historical
      this.svgNodeHist = this.D3ChartHist.setSVG(
        0.5,
        5,
        0.5,
        2,
        0.5,
        25,
        0.5,
        5,
        this.colorTheme,
      );
      this.minMaxYAxis = this.processData.minMax(this.histData, "high", "low");
      this.minMaxYAxis = this.processData.minMax(
        this.fwdData,
        "close",
        "close",
        this.minMaxYAxis,
      );
      if (this.monthData.length > 0) {
        this.minMaxYAxis = this.processData.minMax(
          this.monthData,
          "close",
          "close",
          this.minMaxYAxis,
        );
      }
      let del = 0.1 * (this.minMaxYAxis[1] - this.minMaxYAxis[0]);
      this.minMaxYAxis = [
        this.minMaxYAxis[0] - del,
        this.minMaxYAxis[1] + 2 * del,
      ];
      this.svgNodeHist = this.D3ChartHist.setAxis(
        this.svgNodeHist,
        this.histData,
        this.minMaxYAxis,
        dateFormat,
        60,
        8,
        false,
        [],
        true,
        true,
        true,
        format(currencyFormat),
        true,
      );
      // === add historical candles
      if (this.isFEP) {
        this.D3ChartHist.bars(
          this.svgNodeHist,
          this.histData,
          0.85,
          this.hoverName,
          colorExpo(this.colorTheme),
          "rgba(0,0,0,0)",
          "date",
          "close",
          true,
          false,
          false,
          2,
          1,
          true,
          "id",
        );
      } else {
        if (this.isCandles) {
          this.svgNodeHist = this.D3ChartHist.candles(
            this.svgNodeHist,
            this.histData,
            colorExpo(this.colorTheme),
            grayLightBackground(this.colorTheme),
            this.hoverName,
            0.6,
            0.6,
            colorExpo(this.colorTheme),
            grayLightBackground(this.colorTheme),
            1,
            false,
            null,
            null,
            true,
            "id",
          );
        } else {
          this.svgNodeHist = this.D3ChartHist.line(
            this.svgNodeHist,
            this.histData,
            colorExpo(this.colorTheme),
            1.5,
            "close",
          );
        }
      }

      // === forwards
      this.svgNodeFwd = this.D3ChartFwd.setSVG(
        0.5,
        5,
        0.5,
        70,
        0.5,
        25,
        0.5,
        0,
        this.colorTheme,
      );
      this.svgNodeFwd = this.D3ChartFwd.setAxis(
        this.svgNodeFwd,
        this.fwdData,
        this.minMaxYAxis,
        this.monthDate,
        60,
        8,
        false,
        [],
        true,
        true,
        true,
        format(currencyFormat),
        false,
      );
      // Divider - Vertical Line
      this.svgNodeFwd
        .append("line")
        .attr("stroke-width", 1)
        .attr("stroke", grayLightBackground(this.colorTheme))
        .attr("x1", this.svgNodeFwd.margin.left)
        .attr("y1", 0)
        .attr("x1", this.svgNodeFwd.margin.left)
        .attr("y2", this.svgNodeFwd.height);
      // === add this month's candles
      if (this.monthData.length > 0) {
        if (this.isCandles) {
          this.svgNodeFwd = this.D3ChartHist.candles(
            this.svgNodeFwd,
            this.monthData,
            this.fwdColor,
            grayLightBackground(this.colorTheme),
            this.hoverName + "b",
            0.6,
            0.6,
            this.fwdColor,
            grayLightBackground(this.colorTheme),
            1,
            true,
            1 / 22,
            this.fwdData.map((d) => d.date),
          );
        } else {
          this.svgNodeFwd = this.D3ChartHist.line(
            this.svgNodeFwd,
            this.monthData,
            this.fwdColor,
            1.5,
            "close",
            true,
            1 / 22,
            this.fwdData.map((d) => d.date),
          );
        }
      }
      // === add fwd rates
      this.D3ChartFwd.bars(
        this.svgNodeFwd,
        this.fwdData,
        0.8,
        this.hoverName + "b",
        this.fwdColor,
        "rgba(0,0,0,0)",
        "date",
        "close",
        true,
        false,
        false,
        2,
        1,
        true,
        "id",
      );

      // === custom hover effects and info
      this.D3HoverMktHist.hoverFct(
        this.svgNodeHist,
        this.histData,
        this.hoverName,
        format(currencyFormat),
        dateFormat,
        this.interval.toUpperCase(),
        format(currencyFormat),
        this.svgNodeHist,
        this.svgNodeFwd,
        1,
        this.isCandles,
        this.allData,
        this.isFEP,
        this.colorTheme,
      );
      this.D3HoverMktHistb.hoverFct(
        this.svgNodeFwd,
        this.fwdData,
        this.hoverName + "b",
        format(currencyFormat),
        dateFormat,
        "MONTH",
        format(currencyFormat),
        this.svgNodeHist,
        this.svgNodeFwd,
        2,
        this.isCandles,
        this.allData,
        this.isFEP,
        this.colorTheme,
      );
    },
  },
};
</script>

<style scoped></style>
